import { useState, useEffect, useMemo, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import apiService from '../services/ApiService';
import groupService from '../services/GroupService';
import rankingService from '../services/RankingService';
import LoadingSpinner from './LoadingSpinner';
import { debugError } from '../utils/debugUtils';

const GRANULARITIES = [
  { value: 'DAILY', labelKey: 'leaderboard.comparisonDaily', defaultPeriods: 14, maxPeriods: 90, step: 14 },
  { value: 'WEEKLY', labelKey: 'leaderboard.comparisonWeekly', defaultPeriods: 8, maxPeriods: 52, step: 8 },
  { value: 'MONTHLY', labelKey: 'leaderboard.comparisonMonthly', defaultPeriods: 6, maxPeriods: 36, step: 6 },
  { value: 'YEARLY', labelKey: 'leaderboard.comparisonYearly', defaultPeriods: 5, maxPeriods: 10, step: 5 },
];

const MAX_PLAYERS = 8;

/** Formatea el periodKey del backend ("2026-W25") para mostrarlo en el eje del gráfico ("S25"). */
function formatPeriodTick(period, granularityValue) {
  if (granularityValue === 'WEEKLY') {
    const match = /^(\d{4})-W(\d{2})$/.exec(period);
    if (match) return `S${match[2]}`;
  }
  return period;
}

const LINE_COLORS = [
  '#5B8DEF', '#EF5B5B', '#4CAF50', '#FFB300',
  '#9C27B0', '#00ACC1', '#FF7043', '#8D6E63',
];

export default function RankingComparisonChart({ groupId, initialTopEntries, t }) {
  const [granularityIdx, setGranularityIdx] = useState(1);
  const granularity = GRANULARITIES[granularityIdx];
  const [periods, setPeriods] = useState(granularity.defaultPeriods);

  const [players, setPlayers] = useState([]);
  const [selected, setSelected] = useState([]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carga la lista de jugadores disponibles para el buscador (miembros del grupo, o todos los jugadores en el ranking general).
  useEffect(() => {
    const loadPlayers = groupId
      ? groupService.listMembers(groupId).then((data) =>
          (Array.isArray(data) ? data : []).map((m) => ({ userId: m.userId, name: m.name }))
        )
      : apiService.get('/api/v1/game/scores').then((data) =>
          (Array.isArray(data) ? data : []).map((p) => ({ userId: p.userId, name: p.name }))
        );

    loadPlayers
      .then((list) => setPlayers(list))
      .catch((err) => debugError('[RankingComparisonChart] Error loading players:', err));
  }, [groupId]);

  // Preselecciona el top 5 recibido de la pestaña padre.
  useEffect(() => {
    const top = (initialTopEntries || [])
      .slice(0, 5)
      .filter((p) => p.userId != null)
      .map((p) => ({ userId: p.userId, name: p.name }));
    if (top.length > 0) setSelected(top);
  }, [initialTopEntries]);

  useEffect(() => {
    setPeriods(granularity.defaultPeriods);
  }, [granularityIdx]);

  const selectedUserIds = useMemo(() => selected.map((p) => p.userId), [selected]);

  useEffect(() => {
    if (selectedUserIds.length === 0) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const params = { userIds: selectedUserIds, granularity: granularity.value, periods };
    const request = groupId
      ? groupService.getRankingComparison(groupId, params)
      : rankingService.getComparison(params);

    request
      .then((res) => setData(res))
      .catch((err) => {
        debugError('[RankingComparisonChart] Error loading comparison:', err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [groupId, granularity.value, periods, selectedUserIds.join(',')]);

  const handleAddPlayer = useCallback((_, player) => {
    if (!player) return;
    setSelected((prev) => {
      if (prev.some((p) => p.userId === player.userId) || prev.length >= MAX_PLAYERS) return prev;
      return [...prev, player];
    });
  }, []);

  const handleRemovePlayer = useCallback((userId) => {
    setSelected((prev) => prev.filter((p) => p.userId !== userId));
  }, []);

  const chartRows = useMemo(() => {
    if (!data) return [];
    return data.periods.map((period, idx) => {
      const row = { period: formatPeriodTick(period, granularity.value) };
      data.series.forEach((s) => {
        row[s.name] = s.values[idx];
      });
      return row;
    });
  }, [data, granularity.value]);

  const canLoadMore = periods < granularity.maxPeriods;

  return (
    <Box>
      <Tabs
        value={granularityIdx}
        onChange={(_, idx) => setGranularityIdx(idx)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        {GRANULARITIES.map((g) => (
          <Tab key={g.value} label={t(g.labelKey)} />
        ))}
      </Tabs>

      <Autocomplete
        options={players.filter((p) => !selected.some((s) => s.userId === p.userId))}
        getOptionLabel={(p) => p.name || ''}
        isOptionEqualToValue={(a, b) => a.userId === b.userId}
        onChange={handleAddPlayer}
        value={null}
        disabled={selected.length >= MAX_PLAYERS}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('leaderboard.comparisonAddPlayer')}
            placeholder={selected.length >= MAX_PLAYERS ? t('leaderboard.comparisonMaxPlayers') : ''}
          />
        )}
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {selected.map((p, idx) => (
          <Chip
            key={p.userId}
            label={p.name}
            onDelete={() => handleRemovePlayer(p.userId)}
            sx={{
              borderLeft: `4px solid ${LINE_COLORS[idx % LINE_COLORS.length]}`,
              fontWeight: 600,
            }}
          />
        ))}
      </Box>

      {selected.length === 0 ? (
        <Typography textAlign="center" color="text.secondary" py={4}>
          {t('leaderboard.comparisonNoPlayers')}
        </Typography>
      ) : loading ? (
        <Box sx={{ py: 4 }}>
          <LoadingSpinner />
        </Box>
      ) : error ? (
        <Typography textAlign="center" color="error" py={4}>
          {t('common.error')}
        </Typography>
      ) : (
        <>
          <Box sx={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              <LineChart data={chartRows} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <RechartsTooltip />
                <Legend />
                {(data?.series || []).map((s, idx) => (
                  <Line
                    key={s.userId}
                    type="monotone"
                    dataKey={s.name}
                    stroke={LINE_COLORS[idx % LINE_COLORS.length]}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {canLoadMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Button
                size="small"
                onClick={() => setPeriods((p) => Math.min(granularity.maxPeriods, p + granularity.step))}
              >
                {t('leaderboard.comparisonLoadMore')}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
